import Phaser from 'phaser';
import { GameState } from './GameState';
import { ProceduralAudio } from './ProceduralAudio';
import type { BgmName, SfxType } from './ProceduralAudio';

// Re-export for convenience so scenes don't need to import ProceduralAudio directly
export type { BgmName, SfxType };

class AudioManagerImpl {
  private currentBgm: Phaser.Sound.BaseSound | null = null;
  private currentScene: Phaser.Scene | null = null;
  private currentBgmKey: string | null = null;

  /** Must be called on first user gesture (pointerdown) to unlock AudioContext. */
  init(): void {
    ProceduralAudio.init();
    this._syncVolume();
  }

  setScene(scene: Phaser.Scene): void {
    this.currentScene = scene;
  }

  /** 씬별 BGM 플레이리스트 — 입장할 때마다 랜덤 선택 */
  private playlists: Record<string, string[]> = {
    home:     ['home', 'home_alt1', 'home_alt2'],
    closet:   ['closet', 'closet_alt1', 'closet_alt2'],
    minigame: ['minigame', 'minigame_alt1', 'minigame_alt2'],
    runway:   ['runway', 'runway_alt1', 'runway_alt2'],
    petroom:  ['petroom', 'petroom_alt1', 'petroom_alt2'],
    shop:     ['shop', 'shop_alt1', 'shop_alt2'],
    math:     ['math', 'math_alt1', 'math_alt2'],
  };

  /** 같은 곡을 연달아 뽑지 않도록 직전 곡 기록 */
  private lastPicked: Record<string, string | null> = {};

  private _pickFromPlaylist(key: string): string {
    const list = this.playlists[key];
    if (!list || list.length === 0) return key;

    // 실제 캐시에 있는 트랙만 후보로 (로드 실패 등 대비)
    const available = list.filter(k => this.currentScene?.cache.audio.exists(k));
    const pool = available.length > 0 ? available : list;

    // 직전과 다른 곡 우선
    const last = this.lastPicked[key];
    const candidates = pool.length > 1 ? pool.filter(k => k !== last) : pool;
    const picked = candidates[Math.floor(Math.random() * candidates.length)];
    this.lastPicked[key] = picked;
    return picked;
  }

  playBgm(key: string): void {
    // 같은 카테고리 BGM이 이미 재생 중이면 유지 (재진입 시 재시작 방지)
    if (this.currentBgmKey === key || (this.playlists[key]?.includes(this.currentBgmKey ?? ''))) return;

    this.stopBgm();
    this.currentBgmKey = key;

    // 플레이리스트에서 랜덤으로 한 곡 선택
    const trackKey = this.playlists[key] ? this._pickFromPlaylist(key) : key;

    // Try Phaser asset first
    if (this.currentScene?.cache.audio.exists(trackKey)) {
      this.currentBgm = this.currentScene.sound.add(trackKey, {
        loop: true,
        volume: GameState.data.settings.bgmVolume,
      });
      if (!GameState.data.settings.muted) this.currentBgm.play();
      return;
    }

    // Fall back to procedural synthesis (BgmName 키만 지원)
    if (!GameState.data.settings.muted) {
      ProceduralAudio.playMelody(key as BgmName);
    }
  }

  /**
   * 리듬게임 전용 — 강제로 procedural 멜로디를 특정 BPM으로 재생.
   * 노트 스폰을 음악 비트와 정확히 동기화하기 위해 사용한다.
   * @returns BGM이 시작된 audio-context 시각(sec). 동기화 기준점으로 사용.
   */
  playBgmProceduralAtBpm(key: string, bpm: number): number | null {
    this.stopBgm();
    this.currentBgmKey = key;
    if (GameState.data.settings.muted) return null;
    ProceduralAudio.playMelody(key as BgmName, bpm);
    return ProceduralAudio.getCurrentBgmStartTime();
  }

  /** AudioContext 현재 시각(sec). 리듬 동기화 시 경과 비트 계산용. */
  getAudioTime(): number | null {
    return ProceduralAudio.getAudioContextTime();
  }

  stopBgm(): void {
    this.currentBgmKey = null;
    if (this.currentBgm) {
      this.currentBgm.stop();
      this.currentBgm = null;
    }
    ProceduralAudio.stopMelody();
  }

  playSfx(key: string): void {
    if (GameState.data.settings.muted) return;

    // Try Phaser asset first
    if (this.currentScene?.cache.audio.exists(key)) {
      this.currentScene.sound.play(key, { volume: GameState.data.settings.sfxVolume });
      return;
    }

    // Fall back to procedural synthesis
    ProceduralAudio.playSfx(key as SfxType);
  }

  setMuted(muted: boolean): void {
    GameState.data.settings.muted = muted;
    ProceduralAudio.setMuted(muted);
    if (muted) {
      if (this.currentBgm) this.currentBgm.stop();
      ProceduralAudio.stopMelody();
    } else if (this.currentBgmKey) {
      // Resume procedural BGM if there was one playing
      ProceduralAudio.playMelody(this.currentBgmKey as BgmName);
    }
    GameState.persist();
  }

  setBgmVolume(v: number): void {
    GameState.data.settings.bgmVolume = v;
    ProceduralAudio.setBgmVolume(v);
    if (this.currentBgm) {
      (this.currentBgm as Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound).setVolume(v);
    }
    GameState.persist();
  }

  setSfxVolume(v: number): void {
    GameState.data.settings.sfxVolume = v;
    ProceduralAudio.setSfxVolume(v);
    GameState.persist();
  }

  private _syncVolume(): void {
    const s = GameState.data.settings;
    ProceduralAudio.setBgmVolume(s.bgmVolume);
    ProceduralAudio.setSfxVolume(s.sfxVolume);
    ProceduralAudio.setMuted(s.muted);
  }
}

export const AudioManager = new AudioManagerImpl();
